import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import React, { useEffect } from "react";
import Form from "src/components/base/form";
import { useTranslation } from "react-i18next";
import { useForm, useStore } from "@tanstack/react-form";
import { Fieldset } from "@headlessui/react";
import { Description, ErrorMessage, Field, FieldGroup, Label } from "src/components/base/fieldset";
import { Switch, SwitchField } from "src/components/base/switch";
import { Input } from "src/components/base/input";
import { BackButton, Button, PrimaryButton } from "src/components/base/button";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { notify } from "src/components/base/toast";
import WS from "src/api/ws";
import { Textarea } from "src/components/base/textarea";
import {
    ArrowDownTrayIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ClipboardDocumentListIcon,
} from "@heroicons/react/20/solid";
import { Text } from "src/components/base/text";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { Divider } from "src/components/base/divider";
import { toast } from "react-toastify/unstyled";
import { isFormError } from "src/utils/form-error";
import { CopyButton } from "src/components/base/copy-button";

/**
 * The properties for {@link OidcSettings}
 */
type OidcSettingsProps = {};

/**
 * The OIDC settings page
 */
function OidcSettings(props: OidcSettingsProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const provider = Route.useLoaderData();
    const { providerId } = Route.useParams();

    const [openCert, setOpenCert] = React.useState(false);
    const menuVariants: Variants = {
        collapsed: { height: 0, opacity: 0, overflow: "hidden" },
        expanded: { height: "auto", opacity: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    };

    const MOCK_SECRET = "*".repeat(30);

    const form = useForm({
        defaultValues: {
            name: provider.name,
            login_button_display_english: provider.login_button_display_english,
            login_button_display_german: provider.login_button_display_german,
            enabled: provider.enabled,
            primary: provider.primary,
            issuer_url: provider.url,
            client_id: provider.client_id,
            client_secret: provider.client_secret ? MOCK_SECRET : "",
            redirect_url: provider.redirect_url,
            use_cert: provider.use_cert,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const updated_secret = value.client_secret === MOCK_SECRET ? undefined : value.client_secret;
                const res = await ApiInternal.oidcProvider.update(providerId, {
                    name: value.name,
                    login_button_display_english: value.login_button_display_english,
                    login_button_display_german: value.login_button_display_german,
                    client_id: value.client_id,
                    client_secret: updated_secret ?? null,
                    enabled: value.enabled,
                    primary: value.primary,
                    url: value.issuer_url,
                    use_cert: value.use_cert,
                });

                if (isFormError(res)) {
                    return {
                        fields: {
                            name: res.error.name_already_exist ? t("error.name-already-exist") : undefined,
                        },
                    };
                }

                form.reset();
                await router.invalidate({ sync: true });
                notify.success(t("toast.successfully-updated"));
            },
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);
    const useCert = useStore(form.store, (store) => store.values.use_cert);

    useEffect(() => {
        const listener = WS.addEventListener("message.OidcClientChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    /**
     * Downloads the certificate.
     */
    const downloadCert = async () => {
        const blob = new Blob([provider.cert_pem], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "ztna-admin-cert.pem";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Form className={"flex max-w-lg flex-col gap-6"} onSubmit={form.handleSubmit}>
            <BackButton className={"self-start"} href={"/a/settings/oidc"}>
                {t("button.back-to-oidc-settings")}
            </BackButton>
            {provider.errors && (
                <span className={"text-base/6 text-red-600 data-disabled:opacity-50 sm:text-sm/6 dark:text-red-500"}>
                    {provider.errors}
                </span>
            )}
            <Fieldset>
                <FieldGroup>
                    <SwitchField className={"mb-4"}>
                        <Label>{t("label.enable-oidc")}</Label>
                        <Switch
                            color={"blue"}
                            checked={provider.enabled}
                            onChange={async (enabled) => {
                                await ApiInternal.oidcProvider.update(providerId, { enabled });
                                await router.invalidate();
                            }}
                        />
                    </SwitchField>

                    <SwitchField className={"mb-4"}>
                        <Label>{t("label.primary-oidc")}</Label>
                        <Description>{t("description.primary-oidc")}</Description>
                        <Switch
                            color={"blue"}
                            checked={provider.primary}
                            onChange={async (primary) => {
                                await ApiInternal.oidcProvider.update(providerId, { primary });
                                await router.invalidate();
                            }}
                        />
                    </SwitchField>

                    <Divider className={"mb-4"} />
                </FieldGroup>

                <FieldGroup>
                    <form.Field name={"name"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.name")}</Label>
                                <Description>{t("description.name")}</Description>
                                <Input
                                    type={"text"}
                                    placeholder={""}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"login_button_display_english"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{`${t("label.login-button-display-text")} (${tg("label.english")})`}</Label>
                                <Description>{`${t("description.login-button-display-text")} (${tg("label.english")})`}</Description>
                                <Input
                                    type={"text"}
                                    placeholder={""}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"login_button_display_german"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{`${t("label.login-button-display-text")} (${tg("label.german")})`}</Label>
                                <Description>{`${t("description.login-button-display-text")} (${tg("label.german")})`}</Description>
                                <Input
                                    type={"text"}
                                    placeholder={""}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"issuer_url"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.issuer-url")}</Label>
                                <Description>{t("description.issuer-url")}</Description>
                                <Input
                                    type={"url"}
                                    placeholder={"https://example.com"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"client_id"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.client-id")}</Label>
                                <Description>{t("description.client-id")}</Description>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>

                    <form.Field name={"redirect_url"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.redirect-url")}</Label>
                                <Description>{t("description.redirect-url")}</Description>
                                <div className={"flex flex-row gap-2 pt-2"}>
                                    <Input
                                        disabled={true}
                                        type={"url"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    <CopyButton value={fieldApi.state.value} />
                                </div>
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>

                    <Divider />

                    <form.Field name={"use_cert"}>
                        {(fieldApi) => (
                            <SwitchField className={"mb-4"}>
                                <Label>{t("label.use-cert")}</Label>
                                <Description>{t("description.use-cert")}</Description>
                                <Switch
                                    color={"blue"}
                                    checked={fieldApi.state.value}
                                    onChange={(use_cert) => fieldApi.handleChange(use_cert)}
                                />
                            </SwitchField>
                        )}
                    </form.Field>
                </FieldGroup>

                <FieldGroup>
                    {useCert ? (
                        <div className={"flex flex-col gap-2"}>
                            <div className={"flex items-center justify-between"}>
                                <div
                                    className={"flex w-full items-center gap-x-2 hover:cursor-pointer"}
                                    onClick={() => setOpenCert(!openCert)}
                                >
                                    <Text>
                                        {openCert ? (
                                            <ChevronDownIcon className={"size-4"} />
                                        ) : (
                                            <ChevronRightIcon className={"size-4"} />
                                        )}
                                    </Text>
                                    <Label>{t("label.cert")}</Label>
                                </div>
                                <div className={"flex items-center gap-x-2"}>
                                    <Button
                                        plain={true}
                                        onClick={async () => {
                                            await navigator.clipboard.writeText(provider.cert_pem);
                                            toast.success(tg("toast.copied-to-clipboard"));
                                        }}
                                    >
                                        <ClipboardDocumentListIcon />
                                    </Button>
                                    <Button plain={true} onClick={async () => await downloadCert()}>
                                        <ArrowDownTrayIcon />
                                    </Button>
                                </div>
                            </div>
                            <AnimatePresence initial={false}>
                                {openCert && (
                                    <motion.div
                                        initial={"collapsed"}
                                        animate={"expanded"}
                                        exit={"collapsed"}
                                        variants={menuVariants}
                                    >
                                        <Textarea rows={20} value={provider.cert_pem} readOnly={true} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <form.Field name={"client_secret"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.client-secret")}</Label>
                                    <Description>{t("description.client-secret")}</Description>
                                    <Input
                                        type={"password"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                    )}
                </FieldGroup>
            </Fieldset>
            <div className={"flex justify-end gap-3"}>
                <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                    {t("button.reset")}
                </Button>
                <PrimaryButton type="submit"> {t("button.save")}</PrimaryButton>
            </div>
            <BlockUnsavedChanges condition={isDirty} />
        </Form>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/oidc/$providerId/")({
    component: OidcSettings,
    loader: async ({ params }) => await ApiInternal.oidcProvider.getSingle(params.providerId),
});
